import VuexPersist from "vuex-persist";
import localforage from "localforage";

const isBrowserStorageUnavailableError = (error) => {
  const message = error?.message || "";

  return (
    error?.name === "AbortError" ||
    error?.name === "UnknownError" ||
    /The transaction was aborted, so the request cannot be fulfilled/i.test(message) ||
    /Connection is closing because of: Force close delete origin/i.test(message) ||
    /indexeddb/i.test(message)
  );
};

// localforage rejects its readiness promise when IndexedDB cannot be opened
// ("UnknownError: Internal error opening backing store for indexedDB.open" — private
// browsing, quota, corrupted store, blocked permissions). A failed reconnect after the live
// connection drops builds a NEW readiness chain that nothing consumes, so a one-time handler
// is not enough: it must be re-attached whenever an operation fails. Every operation below
// already falls back to localStorage, so the rejection itself carries no information.
const swallowReadinessRejection = () => {
  localforage.ready().catch(() => {
    // Expected when IndexedDB is unavailable — the localStorage fallback takes over.
  });
};

swallowReadinessRejection();

const readFromLocalStorage = (key) => {
  try {
    return window.localStorage.getItem(key);
  } catch (error) {
    console.warn("localStorage read failed", error);
    return null;
  }
};

const writeToLocalStorage = (key, value) => {
  try {
    window.localStorage.setItem(key, value);
  } catch (error) {
    console.warn("localStorage write failed", error);
  }
};

const removeFromLocalStorage = (key) => {
  try {
    window.localStorage.removeItem(key);
  } catch (error) {
    console.warn("localStorage remove failed", error);
  }
};

const storage = {
  async getItem(key) {
    try {
      return await localforage.getItem(key);
    } catch (error) {
      swallowReadinessRejection();
      if (!isBrowserStorageUnavailableError(error)) {
        console.warn("Persisted store read failed", error);
      }
      return readFromLocalStorage(key);
    }
  },
  async setItem(key, value) {
    try {
      await localforage.setItem(key, value);
      return value;
    } catch (error) {
      swallowReadinessRejection();
      if (!isBrowserStorageUnavailableError(error)) {
        console.warn("Persisted store write failed", error);
      }
      writeToLocalStorage(key, value);
      return value;
    }
  },
  async removeItem(key) {
    try {
      await localforage.removeItem(key);
    } catch (error) {
      swallowReadinessRejection();
      if (!isBrowserStorageUnavailableError(error)) {
        console.warn("Persisted store remove failed", error);
      }
    }
    removeFromLocalStorage(key);
  },
};

const vuexPersist = new VuexPersist({
  key: "clear_intent",
  storage,
});

export default vuexPersist;
